import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Paper,
  Button,
  Group,
  Stack,
  Text,
  TextInput,
  NumberInput,
  Select,
  Divider,
  Grid,
  Badge,
} from '@mantine/core';
import { ProcessedBetSlipData } from '../../../backend/src/models/BetSlip';
import { saveBetSlip, checkTicketIdExists } from '../services/api';
import { MarketType } from '../types/betslip';

export function BetSlipReview() {
  const location = useLocation();
  const navigate = useNavigate();
  const [processedData, setProcessedData] = useState<ProcessedBetSlipData | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    const state = location.state as { processedData: ProcessedBetSlipData; imageUrl: string } | null;
    if (state) {
      // Always ensure there's at least one selection to edit, even if none were extracted
      const initialSelections = state.processedData.selections?.length ?
        state.processedData.selections :
        [{
          sport: '',
          league: '',
          eventDateTime: undefined,
          homeTeam: '',
          awayTeam: '',
          selectedTeam: '',
          marketType: 'money_line' as const, // Specify the exact type
          line: undefined,
          odds: '',
        }];

      setProcessedData({
        ...state.processedData,
        selections: initialSelections
      });
      setImageUrl(state.imageUrl);
    } else {
      navigate('/upload');
    }
  }, [location, navigate]);

  const handleSave = async () => {
    if (processedData) {
      try {
        // First check if ticket ID exists
        if (processedData.ticketId) {
          const result = await checkTicketIdExists(processedData.ticketId);
          if (result.exists) {
            alert(`A bet slip with ticket ID ${processedData.ticketId} already exists`);
            return;
          }
        }
        
        await saveBetSlip(processedData);
        navigate('/bets');
      } catch (error) {
        console.error('Error saving bet slip:', error);
        alert('Failed to save bet slip. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    navigate('/upload');
  };

  const updateSelection = (index: number, field: keyof NonNullable<ProcessedBetSlipData['selections']>[0], value: any) => {
    if (!processedData?.selections) return;

    const newSelections = [...processedData.selections];
    const selection = newSelections[index] || {};
    newSelections[index] = {
      ...selection,
      [field]: value,
    };

    setProcessedData({
      ...processedData,
      selections: newSelections,
    });
  };

  const addSelection = () => {
    if (!processedData) return;

    setProcessedData({
      ...processedData,
      selections: [
        ...(processedData.selections || []),
        {
          sport: '',
          league: '',
          eventDateTime: undefined,
          homeTeam: '',
          awayTeam: '',
          selectedTeam: '',
          marketType: 'money_line',
          line: undefined,
          odds: '',
        },
      ],
    });
  };

  const removeSelection = (index: number) => {
    if (!processedData?.selections) return;

    const newSelections = processedData.selections.filter((_, i) => i !== index);
    setProcessedData({
      ...processedData,
      selections: newSelections,
    });
  };

  if (!processedData || !imageUrl) {
    return null;
  }

  return (
    <Container size="lg">
      <Title order={2} mb="xl">
        Review Bet Slip
      </Title>

      <Paper shadow="xs" p="md" mb="xl">
        <Group position="apart" mb="xl">
          <div>
            <Title order={3}>Extracted Data</Title>
            <Text size="sm" color="dimmed">
              Please review and correct any information if needed
            </Text>
            {processedData.ocrConfidence && (
              <Badge color={processedData.ocrConfidence > 0.8 ? 'green' : 'yellow'}>
                OCR Confidence: {Math.round(processedData.ocrConfidence * 100)}%
              </Badge>
            )}
          </div>
          <img
            src={imageUrl}
            alt="Bet slip"
            style={{ maxWidth: '200px', maxHeight: '300px', objectFit: 'contain' }}
          />
        </Group>

        <Stack spacing="md">
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                label="Ticket ID"
                value={processedData.ticketId || ''}
                onChange={(e) =>
                  setProcessedData({ ...processedData, ticketId: e.target.value })
                }
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Casino Name"
                value={processedData.casinoName || ''}
                onChange={(e) =>
                  setProcessedData({ ...processedData, casinoName: e.target.value })
                }
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={6}>
              <NumberInput
                label="Wager Amount"
                value={processedData.wagerAmount || 0}
                onChange={(value) =>
                  setProcessedData({ ...processedData, wagerAmount: value || 0 })
                }
                precision={2}
                min={0}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <NumberInput
                label="Potential Payout"
                value={processedData.potentialPayout || 0}
                onChange={(value) =>
                  setProcessedData({ ...processedData, potentialPayout: value || 0 })
                }
                precision={2}
                min={0}
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={6}>
              <Text size="sm" weight={500} mb={4}>Event Date/Time</Text>
              <Group grow>
                <input
                  type="date"
                  value={processedData.eventDateTime ? new Date(processedData.eventDateTime).toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    const date = new Date(e.target.value);
                    if (!isNaN(date.getTime())) {
                      setProcessedData({ ...processedData, eventDateTime: date });
                    }
                  }}
                  style={{ padding: '6px', border: '1px solid #ced4da', borderRadius: '4px' }}
                />
                <input
                  type="time"
                  value={processedData.eventDateTime ? new Date(processedData.eventDateTime).toTimeString().substring(0,5) : ''}
                  onChange={(e) => {
                    const currentDate = processedData.eventDateTime ? new Date(processedData.eventDateTime) : new Date();
                    const [hours, minutes] = e.target.value.split(':').map(Number);
                    currentDate.setHours(hours, minutes);
                    setProcessedData({ ...processedData, eventDateTime: currentDate });
                  }}
                  style={{ padding: '6px', border: '1px solid #ced4da', borderRadius: '4px', marginLeft: '8px' }}
                />
              </Group>
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Window Number"
                value={processedData.windowNumber || ''}
                onChange={(e) =>
                  setProcessedData({ ...processedData, windowNumber: e.target.value })
                }
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={12}>
              <TextInput
                label="Barcode Data"
                value={processedData.barcodeData || ''}
                onChange={(e) =>
                  setProcessedData({ ...processedData, barcodeData: e.target.value })
                }
              />
            </Grid.Col>
          </Grid>

          <Select
            label="Bet Type"
            value={processedData.betType || 'straight'}
            onChange={(value) =>
              setProcessedData({
                ...processedData,
                betType: value as ProcessedBetSlipData['betType'],
              })
            }
            data={[
              { value: 'straight', label: 'Straight' },
              { value: 'parlay', label: 'Parlay' },
              { value: 'teaser', label: 'Teaser' },
              { value: 'round_robin', label: 'Round Robin' },
              { value: 'futures', label: 'Futures' },
              { value: 'prop', label: 'Prop' },
            ]}
          />

          <Divider label="Selections" labelPosition="center" />

          {processedData.selections?.map((selection, index) => (
            <Paper key={index} p="md" withBorder>
              <Group position="apart" mb="md">
                <Title order={4}>Selection {index + 1}</Title>
                <Button
                  variant="subtle"
                  color="red"
                  onClick={() => removeSelection(index)}
                  compact
                >
                  Remove
                </Button>
              </Group>

              <Grid>
                <Grid.Col span={6}>
                  <TextInput
                    label="Sport"
                    value={selection.sport || ''}
                    onChange={(e) => updateSelection(index, 'sport', e.target.value)}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextInput
                    label="League"
                    value={selection.league || ''}
                    onChange={(e) => updateSelection(index, 'league', e.target.value)}
                  />
                </Grid.Col>
              </Grid>

              <Grid>
                <Grid.Col span={12}>
                  <Text size="sm" weight={500} mb={4}>Event Date/Time</Text>
                  <Group grow>
                    <input
                      type="date"
                      value={selection.eventDateTime ? new Date(selection.eventDateTime).toISOString().split('T')[0] : ''}
                      onChange={(e) => {
                        const date = new Date(e.target.value);
                        if (!isNaN(date.getTime())) {
                          updateSelection(index, 'eventDateTime', date);
                        }
                      }}
                      style={{ padding: '6px', border: '1px solid #ced4da', borderRadius: '4px' }}
                    />
                    <input
                      type="time"
                      value={selection.eventDateTime ? new Date(selection.eventDateTime).toTimeString().substring(0,5) : ''}
                      onChange={(e) => {
                        const currentDate = selection.eventDateTime ? new Date(selection.eventDateTime) : new Date();
                        const [hours, minutes] = e.target.value.split(':').map(Number);
                        currentDate.setHours(hours, minutes);
                        updateSelection(index, 'eventDateTime', currentDate);
                      }}
                      style={{ padding: '6px', border: '1px solid #ced4da', borderRadius: '4px', marginLeft: '8px' }}
                    />
                  </Group>
                </Grid.Col>
              </Grid>

              <Grid>
                <Grid.Col span={12}>
                  <Select
                    label="Market Type"
                    value={selection.marketType || 'money_line'}
                    onChange={(value) => updateSelection(index, 'marketType', value as MarketType)}
                    data={[
                      { value: 'money_line', label: 'Money Line' },
                      { value: 'point_spread', label: 'Point Spread' },
                      { value: 'total', label: 'Total' },
                      { value: 'prop', label: 'Prop' },
                      { value: 'futures', label: 'Futures' },
                    ]}
                  />
                </Grid.Col>
              </Grid>

              <Grid>
                <Grid.Col span={6}>
                  <NumberInput
                    label="Line"
                    value={selection.line || 0}
                    onChange={(value) => updateSelection(index, 'line', value)}
                    precision={1}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextInput
                    label="Odds"
                    value={selection.odds || ''}
                    onChange={(e) => updateSelection(index, 'odds', e.target.value)}
                  />
                </Grid.Col>
              </Grid>
            </Paper>
          ))}

          <Button onClick={addSelection} variant="outline" fullWidth>
            Add Selection
          </Button>
        </Stack>
      </Paper>

      <Group position="right" mt="xl">
        <Button variant="outline" onClick={handleCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave}>Save Bet Slip</Button>
      </Group>
    </Container>
  );
}